import React, { Fragment, useCallback } from "react";
import Footnote from "./Footnote";
import ScriptureRef from "./ScriptureRef";
import { ScriptureItem } from "../../api/typedefs/ScriptureItem";

interface ScriptureTagProps {
  tagName: string;
  children: React.ReactElement;
  attrs: React.Attributes;
}

export interface Highlights {
  [highlightName: string]: string[] | undefined;
}

interface ScriptureItemsProps {
  highlights?: Highlights;
  items?: (ScriptureItem | null)[];
  onVerseHover: (verseOrgIds: string[]) => void;
  onVerseSelect: (
    verseOrgIds: string[],
    event: React.MouseEvent<HTMLElement>
  ) => void;
  onVerseUnhover: (verseOrgIds: string[]) => void;
  showRefPreview: (refId: string) => void;
}

interface GetAttrsForItemParams {
  highlights: Highlights;
  index: number;
  item: ScriptureItem;
  onVerseHover: (verseOrgIds: string[]) => void;
  onVerseSelect: (
    verseOrgIds: string[],
    event: React.MouseEvent<HTMLElement>
  ) => void;
  onVerseUnhover: (verseOrgIds: string[]) => void;
}

interface ScriptureItemDomAttrs {
  key: string;
  className: string;
  "data-org-ids"?: string;
  "data-verse-id"?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const tags: Map<string, string> = new Map([
  ["para", "p"],
  ["verse", "sup"],
  ["char", "span"],
  ["verse-span", "span"]
]);

const areVersesInSet = (
  verseSet: string[] = [],
  verseIds: string[] = []
): boolean => {
  return Boolean(verseSet.find(id => verseIds.indexOf(id) !== -1));
};

const getAttrsForItem = ({
  highlights,
  index,
  item,
  onVerseHover,
  onVerseSelect,
  onVerseUnhover
}: GetAttrsForItemParams): ScriptureItemDomAttrs => {
  if (item.name === "verse-span") {
    const { verseOrgIds, verseId } = item.attrs;
    const dataOrgIds = verseOrgIds.join(",");
    const itemHighlights = [];
    for (const highlightType in highlights) {
      if (areVersesInSet(highlights[highlightType], verseOrgIds)) {
        itemHighlights.push(highlightType);
      }
    }
    const className = [item.attrs.class, ...itemHighlights].join(" ");

    return {
      key: [item.attrs.class, ...verseOrgIds, index].join("-"),
      className,
      "data-org-ids": dataOrgIds,
      "data-verse-id": verseId,
      onClick: event => onVerseSelect(verseOrgIds, event),
      onMouseEnter: () => onVerseHover(verseOrgIds),
      onMouseLeave: () => onVerseUnhover(verseOrgIds)
    };
  }

  return {
    key: [item.attrs.style, index].join("-"),
    className: item.attrs.style
  };
};

const ScriptureTag = ({ tagName = "", children, attrs }: ScriptureTagProps) => {
  return React.createElement(tags.get(tagName) || "span", attrs, children);
};

const ScriptureItems = (props: ScriptureItemsProps): React.ReactElement => {
  // NOTE: the below hooks prevent updates from happening to content
  // this allows us to pin down the first received props and avoid
  // excessive and unesscary re-renders
  const onVerseHover = useCallback(props.onVerseHover, []);
  const onVerseUnhover = useCallback(props.onVerseUnhover, []);
  const onVerseSelect = useCallback(props.onVerseSelect, []);
  const showRefPreview = useCallback(props.showRefPreview, []);
  const { highlights = {}, items = [] } = props;

  // TODO: we don't need to wrap this in an empty tag <></>, but I can't quite get the TS types right
  return (
    <>
      {items.map((item, index) => {
        if (item === null) return null;

        switch (item.type) {
          case "text":
            return item.text;
          case "component":
            return <Fragment key={index}>{item.component}</Fragment>;
          case "tag": {
            const key = [item.name, item.attrs.style, index].join("-");
            const attrs = getAttrsForItem({
              item,
              index,
              highlights,
              onVerseSelect,
              onVerseHover,
              onVerseUnhover
            });

            switch (item.name) {
              case "note":
                return (
                  <ScriptureTag key={key} tagName={item.name} attrs={attrs}>
                    <Footnote>
                      <ScriptureItems {...props} items={item.items} />
                    </Footnote>
                  </ScriptureTag>
                );
              case "ref":
                return (
                  <ScriptureTag key={key} tagName={item.name} attrs={attrs}>
                    <ScriptureRef
                      refId={item.attrs.id}
                      showRefPreview={showRefPreview}
                    >
                      <ScriptureItems {...props} items={item.items} />
                    </ScriptureRef>
                  </ScriptureTag>
                );
              default:
                return (
                  <ScriptureTag tagName={item.name} attrs={attrs} key={key}>
                    <ScriptureItems {...props} items={item.items} />
                  </ScriptureTag>
                );
            }
          }
          default:
            return null;
        }
      })}
    </>
  );
};

export default ScriptureItems;
